/**
 * Shared event contract between the BlockSuite selection toolbar
 * (`curiescious-toolbar.ts`) and the `SelectionRouter` that handles
 * them at the app level.
 *
 * Keep this in sync with the `detail` object each toolbar action
 * dispatches. The router consumes these types through `CustomEvent`.
 */

export interface CuriesciousActionDetail {
  /** Raw selected text from the editor. */
  text: string;
  /**
   * Citation tokens (`[PMID:x]`, `[DOI:x]`, `[arXiv:x]`) scanned out
   * of the selection. Preserves provenance so downstream modules
   * (mindmap nodes, manuscript snippets, branched sessions) can keep
   * the citations linked to the quoted material.
   */
  citationTokens: CitationToken[];
  /** Id of the doc the selection came from, when available. */
  sourceDocId?: string;
  /**
   * Id of the block where the selection started — used by the mindmap
   * backlinks so clicking a node can scroll the source passage into
   * view inside the original doc.
   */
  sourceBlockId?: string;
  /**
   * Viewport bounding box of the user's text selection at dispatch
   * time. Used as the anchor for the type-picker popover so the
   * affordance lands next to the highlighted passage instead of
   * jumping to the corner of the screen.
   */
  sourceRect?: { x: number; y: number; width: number; height: number };
  /**
   * Inline range of the selection inside its source block — the
   * `index` and `length` we pass to BlockSuite's `text.format` so we
   * can paint the source highlight on exactly the user-selected slice
   * (no more, no less). Captured by the toolbar's `collectSelection`.
   */
  sourceRange?: { index: number; length: number };
}

/**
 * Discriminator for the typed-node picker on "Add to Mindmap". Each
 * value maps to a branch label + shape + color in NODE_TYPE_REGISTRY.
 */
export type MindmapNodeType =
  | 'finding'
  | 'hypothesis'
  | 'question'
  | 'method'
  | 'quote';

export const MINDMAP_NODE_TYPES: readonly MindmapNodeType[] = [
  'finding',
  'hypothesis',
  'question',
  'method',
  'quote',
] as const;

/**
 * Window event payload fired by the mindmap outline panel when the
 * user clicks a leaf. The detail-page editor listens for this so it
 * can scroll the source passage into view without forcing a mode
 * switch.
 */
export interface ScrollToSourceDetail {
  sourceDocId: string;
  sourceBlockId?: string;
}

export const SCROLL_TO_SOURCE_EVENT = 'curiescious:scroll-to-source' as const;

/**
 * Window event the selection-router fires after a successful mindmap
 * add or undo. The outline panel listens for it so the rendered
 * outline refreshes immediately instead of waiting for the polling
 * tick.
 */
export const MINDMAP_CHANGED_EVENT = 'curiescious:mindmap-changed' as const;

/**
 * Window event fired when the workspace's branch graph changes —
 * a branch is created, undone, or trashed. The inline branches
 * section and the doc-list's parent-collapse UI both subscribe so
 * they re-scan the workspace immediately instead of waiting for a
 * polling tick.
 */
export const BRANCHES_CHANGED_EVENT = 'curiescious:branches-changed' as const;

/**
 * Window event fired when the workspace's notes index changes —
 * an entry is added or undone. The inline notes section subscribes
 * so it refreshes immediately on save/undo instead of waiting for a
 * polling tick.
 */
export const NOTES_CHANGED_EVENT = 'curiescious:notes-changed' as const;

export interface CitationToken {
  /** The raw matched token, e.g. `[PMID:12345]`. */
  raw: string;
  type: 'pubmed' | 'doi' | 'arxiv';
  identifier: string;
}

export const CURIESCIOUS_EVENTS = {
  sendToChat: 'curiescious:send-to-chat',
  addToMindmap: 'curiescious:add-to-mindmap',
  addToNotes: 'curiescious:add-to-notes',
  createBranch: 'curiescious:create-branch',
} as const;

export type CuriesciousEventName =
  (typeof CURIESCIOUS_EVENTS)[keyof typeof CURIESCIOUS_EVENTS];
